import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { catalogError, isRateLimitCode, toClientError } from "@/lib/errors";

/**
 * Create an account through the service-role admin API.
 *
 * WHY THIS EXISTS
 * ---------------
 * This is the only path clients use to create an account. It replaced
 * `supabase.auth.signUp`, which had two problems:
 *
 *   1. It makes GoTrue send its own "confirm your signup" email. That message
 *      comes from Supabase's address, not the Gmail sender this app configures
 *      in `.env`, so every new user also received a verification mail they were
 *      never meant to act on.
 *   2. It calls the public `/signup` endpoint, which Supabase rate limits twice
 *      over: per IP address (`over_request_rate_limit`) and per project email
 *      quota (`over_email_send_rate_limit`). Once either budget is spent, a
 *      brand-new person sees "Too many attempts" with nothing they can do,
 *      because the budget was spent by someone else's traffic.
 *
 * The admin endpoint sends no confirmation email (documented behaviour), so it
 * spends none of the project's email quota, sits outside the public limiter,
 * and leaves `sendOtp`'s branded Gmail message as the only verification mail.
 * Verification is unchanged: `sendOtp` still mails the code and `verifyOtp`
 * still confirms the account.
 *
 * WHAT THIS FUNCTION MUST NOT CHANGE
 * ----------------------------------
 * The public sign-up endpoint answers "that address is already taken" with an
 * obfuscated *success*, so an attacker cannot enumerate registered addresses.
 * This path has to behave the same way or it becomes an enumeration oracle.
 */

const signupSchema = z.object({
  email: z.string().email().max(255),
  password: z.string().min(6).max(100),
  name: z.string().trim().min(1).max(100),
});

/** Raw text of an auth-admin failure, or "" when GoTrue sent none. */
function messageOf(error) {
  return typeof error?.message === "string" ? error.message : "";
}

/** Whether GoTrue refused because the address already has an account. */
function isAlreadyRegistered(error) {
  if (error?.code === "user_already_exists" || error?.code === "email_exists") return true;
  return /already (been )?registered/i.test(messageOf(error));
}

export const createAccount = createServerFn({ method: "POST" })
  .validator((d) => signupSchema.parse(d))
  .handler(async ({ data }) => {
    const email = data.email.trim().toLowerCase();

    const { error } = await supabaseAdmin.auth.admin.createUser({
      email,
      password: data.password,
      user_metadata: { name: data.name.trim() },
      email_confirm: false,
    });

    if (!error) return { success: true };

    // Mirror the public endpoint's obfuscated success. The caller carries on
    // to `sendOtp`, which overwrites any stored code for the address.
    if (isAlreadyRegistered(error)) return { success: true };

    const message = messageOf(error);

    if (isRateLimitCode(error.code)) {
      // Even the admin endpoint was throttled. Report it as what it is rather
      // than letting the HTTP-status mapping turn a 429 into AI copy.
      throw catalogError("AUTH_RATE_LIMITED", { area: "auth.signUp" });
    }

    if (error.code === "signup_disabled") {
      throw catalogError("FORBIDDEN", {
        area: "auth.signUp",
        message: "Sign-ups are turned off on this project.",
      });
    }

    if (error.code === "weak_password" || /\bpassword\b/i.test(message)) {
      throw catalogError("INVALID_INPUT", {
        area: "auth.signUp",
        message: "That password doesn't meet the requirements. Use at least 6 characters.",
      });
    }

    if (error.code === "email_address_invalid" || /\bemail\b/i.test(message)) {
      throw catalogError("INVALID_INPUT", {
        area: "auth.signUp",
        message: "That email address isn't valid.",
      });
    }

    // Everything else: log the raw cause server-side and hand the browser a
    // generic failure. `toClientError` never attaches the raw text for an
    // anonymous caller.
    throw await toClientError(error, { area: "auth.signUp" });
  });
